/**
 * What the composer suggests while a message is written (F21, F38, F70; threads.md T-13):
 * after `@` the context to work on, documents, artifacts, tools and the writer's own
 * memories; after `/` skills and prompt templates. No people: the chat is with the agent.
 * A pick inserts exactly the text the agent reads: `/<skill name>` (processes.md P-9,
 * `reply_skills`), a memory's ID as the turn's memory index lists it, and an item's
 * `<item id>@<version>` otherwise, the reference Add… inserts.
 */

export type SuggestionTrigger = "@" | "/";

/** The `@word` or `/word` being typed right before the caret: what to complete. */
export interface SuggestionQuery {
  readonly trigger: SuggestionTrigger;
  /** Index of the trigger character. */
  readonly start: number;
  /** The text after the trigger up to the caret. */
  readonly query: string;
}

/** A suggestion read from the project's content: named for the list, inserted by `reference`. */
export interface Suggestion {
  readonly kind: "document" | "artifact" | "tool" | "memory" | "skill" | "prompt";
  /** `<item id>@<version>`, or a memory's ID. */
  readonly reference: string;
  /** The document's key, the item's name or the memory's title; a skill's name is its `/` word. */
  readonly name: string;
}

export type SuggestionKind = Suggestion["kind"];

export interface SuggestionSources {
  /** Artifacts, tools, memories, skills and prompts, matched here by name. */
  readonly catalogue: readonly Suggestion[];
  /** Documents the server already matched to the query (`listSourceDocuments`). */
  readonly documents: readonly Suggestion[];
}

/** What each trigger offers, in list order. */
export const SUGGESTION_KINDS: Readonly<Record<SuggestionTrigger, readonly SuggestionKind[]>> = {
  "@": ["document", "artifact", "tool", "memory"],
  "/": ["skill", "prompt"],
};

/** Suggestions of one kind in the list, so every kind stays visible. */
export const MAXIMUM_SUGGESTIONS_PER_KIND = 5;
const MAXIMUM_QUERY_LENGTH = 40;

/** The suggestion query at `caret`, if the caret ends an `@word` or `/word` that starts a word. */
export function suggestionQueryAt(text: string, caret: number): SuggestionQuery | null {
  const before = text.slice(0, caret);
  const start = Math.max(before.lastIndexOf("@"), before.lastIndexOf("/"));
  if (start === -1) {
    return null;
  }
  const query = before.slice(start + 1);
  const startsWord = start === 0 || /\s/.test(before.charAt(start - 1));
  if (!startsWord || /\s/.test(query) || query.length > MAXIMUM_QUERY_LENGTH) {
    return null;
  }
  return { trigger: before.charAt(start) === "@" ? "@" : "/", start, query };
}

/** The suggestions for `query`: names of which a word starts with it, at most a few per kind. */
export function matchSuggestions(query: SuggestionQuery, sources: SuggestionSources): Suggestion[] {
  const wanted = query.query.toLowerCase();
  const matches = (name: string) => name.toLowerCase().startsWith(wanted) || name.toLowerCase().split(/[\s_-]+/).some((word) => word.startsWith(wanted));
  const byKind = (kind: SuggestionKind): readonly Suggestion[] =>
    kind === "document" ? sources.documents : sources.catalogue.filter((item) => item.kind === kind && matches(item.name));
  return SUGGESTION_KINDS[query.trigger].flatMap((kind) => byKind(kind).slice(0, MAXIMUM_SUGGESTIONS_PER_KIND));
}

/** The text a pick inserts. */
export function suggestionText(suggestion: Suggestion): string {
  return suggestion.kind === "skill" ? `/${suggestion.name}` : suggestion.reference;
}

/** A stable key for the list. */
export function suggestionKey(suggestion: Suggestion): string {
  return `${suggestion.kind}:${suggestion.reference}`;
}

/** Replace the query with the chosen suggestion and a space; returns the text and the caret. */
export function insertSuggestion(text: string, query: SuggestionQuery, caret: number, suggestion: Suggestion): { text: string; caret: number } {
  const inserted = `${suggestionText(suggestion)} `;
  return { text: text.slice(0, query.start) + inserted + text.slice(caret), caret: query.start + inserted.length };
}

/** Put `reference` at the caret as its own word, as a pick from the composer's picker does; returns the text and the caret. */
export function insertReferenceAt(text: string, caret: number, reference: string): { text: string; caret: number } {
  const before = text.slice(0, caret);
  const inserted = `${before === "" || /\s$/.test(before) ? "" : " "}${reference} `;
  return { text: before + inserted + text.slice(caret), caret: caret + inserted.length };
}
